










































































import { AxiosError } from 'axios';
import { DataTableHeader } from 'vuetify';

import {
  getAiStreamerGlobalGamesSettings,
  updateAiStreamerGlobalGameSettings
} from '@/api/CopyStake/AiStreamer';
import {
  AiStreamerGameVolatilityNaming,
  EAiStreamerGameVolatility,
  IAiStreamerGlobalGameSettingsParams,
  TAiStreamerGlobalGameSettingsItem,
  TAiStreamerGlobalGameSettingsPayload,
  ValidationRule
} from '@/api/schema';
import { errorToastMessage } from '@/helpers/errorToastMessage';
import formatDate from '@/helpers/formatDate';
import * as vr from '@/helpers/validation';
import SuperAdminAiStreamerGamesSettingsFilter from '@/views/SuperAdminViews/super-admin-configuration/SuperAdminAiStreamerGamesSettingsFilter.vue';

interface IEditableRow {
  bonusBuyCostCoefficient: number | string | null;
  volatility: EAiStreamerGameVolatility | null;
  potential: number | string | null;
}

interface IFilterData {
  searchBy: string;
  searchPhrase: string;
}

const defaultOptions = {
  page: 1,
  itemsPerPage: 10,
  sortBy: ['updatedAt'],
  sortDesc: [true]
};

export default {
  name: 'SuperAdminAiStreamerGamesSettings',
  components: {
    SuperAdminAiStreamerGamesSettingsFilter
  },
  data(): any {
    return {
      gamesSettingsList: [] as TAiStreamerGlobalGameSettingsItem[],
      editableRows: {} as Record<number, IEditableRow>,

      appliedFilter: null as { [key: string]: string } | null,

      total: 0,
      options: { ...defaultOptions },
      loading: {
        list: false,
        save: {} as Record<number, boolean>,
        saveAll: false
      }
    };
  },
  computed: {
    headers(): DataTableHeader[] {
      return [
        { text: 'Game Name', value: 'gameName' },
        { text: 'Provider', value: 'provider' },
        { text: 'Game Code', value: 'gameCode' },
        {
          text: 'Bonus Buy Coefficient',
          value: 'bonusBuyCostCoefficient',
          width: 140
        },
        {
          text: 'Volatility',
          value: 'volatility',
          width: 180
        },
        {
          text: 'Potential',
          value: 'potential',
          width: 140
        },
        { text: '', value: 'actions', width: 50, sortable: false },
        {
          text: 'Updated At',
          value: 'updatedAt',
          width: 180
        }
      ];
    },
    volatilityOptions(): {
      text: string;
      value: EAiStreamerGameVolatility;
    }[] {
      return Object.values(EAiStreamerGameVolatility).map((volatility) => ({
        text: AiStreamerGameVolatilityNaming[volatility],
        value: volatility
      }));
    },
    changedRows(): TAiStreamerGlobalGameSettingsItem[] {
      return this.gamesSettingsList.filter(
        (item: TAiStreamerGlobalGameSettingsItem) =>
          !this.loading.save[item.id] &&
          this.isRowChanged(item.id) &&
          this.isRowValid(item.id)
      );
    },
    hasChangedRows(): boolean {
      return this.changedRows.length > 0;
    },
    amountRules(): ValidationRule[] {
      return [
        (v: number | string | null) =>
          v === null || v === '' || vr.validNumber(String(v)),
        (v: number | string | null) =>
          v === null || v === '' || vr.positiveNumber(String(v))
      ];
    }
  },
  methods: {
    formatDate,

    handleUpdateFilter(filters: IFilterData | null): void {
      this.appliedFilter = filters
        ? { [filters.searchBy]: filters.searchPhrase }
        : null;
      this.options.page = 1;

      this.getGamesSettingsList();
    },

    parseRowsData(items: TAiStreamerGlobalGameSettingsItem[]): void {
      items.forEach((item) => {
        this.$set(this.editableRows, item.id, {
          bonusBuyCostCoefficient: item.bonusBuyCostCoefficient,
          volatility: item.volatility,
          potential: item.potential
        });
      });
    },

    isRowChanged(id: number): boolean {
      const original = this.gamesSettingsList.find(
        (item: TAiStreamerGlobalGameSettingsItem) => item.id === id
      );
      const draft = this.editableRows[id];
      if (!original || !draft) return false;

      return (
        this.normalizeAmount(draft.bonusBuyCostCoefficient) !==
          this.normalizeAmount(original.bonusBuyCostCoefficient) ||
        this.normalizeValue(draft.volatility) !==
          this.normalizeValue(original.volatility) ||
        this.normalizeAmount(draft.potential) !==
          this.normalizeAmount(original.potential)
      );
    },
    isRowValid(id: number): boolean {
      const draft = this.editableRows[id];
      if (!draft) return false;

      const isValidAmount = (v: number | string | null): boolean =>
        v === null ||
        v === '' ||
        (vr.validNumber(String(v)) === true &&
          vr.positiveNumber(String(v)) === true);

      return (
        isValidAmount(draft.bonusBuyCostCoefficient) &&
        isValidAmount(draft.potential)
      );
    },
    normalizeValue(v: number | string | null): number | string | null {
      return v === '' || v === undefined ? null : v;
    },
    normalizeAmount(v: number | string | null): number | null {
      const normalized = this.normalizeValue(v);
      return normalized === null ? null : Number(normalized);
    },

    async getGamesSettingsList(): Promise<void> {
      this.loading.list = true;

      const params: IAiStreamerGlobalGameSettingsParams = {
        size: this.options.itemsPerPage,
        page: this.options.page - 1,
        sort: this.options.sortBy[0],
        order: this.options.sortDesc[0] ? 'DESC' : 'ASC',
        ...(this.appliedFilter || {})
      };

      return getAiStreamerGlobalGamesSettings(params)
        .then(({ content, totalElements }) => {
          this.gamesSettingsList = content;
          this.total = totalElements;
          this.parseRowsData(content);
        })
        .catch((error: AxiosError) => {
          errorToastMessage(error);
          this.gamesSettingsList = [];
          this.total = 0;
        })
        .finally(() => {
          this.loading.list = false;
        });
    },

    async updateRow(
      item: TAiStreamerGlobalGameSettingsItem
    ): Promise<boolean> {
      const draft = this.editableRows[item.id];

      const payload: TAiStreamerGlobalGameSettingsPayload = {
        bonusBuyCostCoefficient: this.normalizeAmount(
          draft.bonusBuyCostCoefficient
        ),
        volatility: draft.volatility,
        potential: this.normalizeAmount(draft.potential)
      };

      this.$set(this.loading.save, item.id, true);

      return updateAiStreamerGlobalGameSettings(item.id, payload)
        .then((data: TAiStreamerGlobalGameSettingsItem) => {
          const index = this.gamesSettingsList.findIndex(
            (g: TAiStreamerGlobalGameSettingsItem) => g.id === item.id
          );
          if (index !== -1) {
            this.$set(this.gamesSettingsList, index, data);
          }
          this.parseRowsData([data]);

          return true;
        })
        .catch((error: AxiosError) => {
          errorToastMessage(error);

          return false;
        })
        .finally(() => {
          this.$delete(this.loading.save, item.id);
        });
    },

    async saveRow(item: TAiStreamerGlobalGameSettingsItem): Promise<void> {
      const success = await this.updateRow(item);

      if (success) {
        this.$toast.success(
          `"${item.gameName}" configuration was successfully updated!`
        );
      }
    },

    async saveAllRows(): Promise<void> {
      const targetRows = this.changedRows;
      if (!targetRows.length) return;

      this.loading.saveAll = true;

      const results = await Promise.all(
        targetRows.map((item: TAiStreamerGlobalGameSettingsItem) =>
          this.updateRow(item)
        )
      );

      this.loading.saveAll = false;

      const successCount = results.filter(Boolean).length;
      if (successCount) {
        const noun = successCount > 1 ? 'games were' : 'game was';
        this.$toast.success(`${successCount} ${noun} successfully updated!`);
      }
    }
  }
};
